import Analytics from 'analytics';
import { getCookie } from './utils/cookies';
import { get } from 'lodash';

// Tenant configuration
const TENANT_ID = import.meta.env.VITE_TENANT_ID || 'emg-ai-english-learning';

// Analytics endpoint
const ANALYTICS_ENDPOINT = import.meta.env.VITE_ANALYTICS_ENDPOINT || 'https://tracking.aitechlab.vn/api/analytics/track';

// Analytics filtering configuration
const ANALYTICS_CONFIG = {
    // Events to ignore (exact match)
    ignoredEvents: [
        'test_event',
        'debug_click',
        'development_action'
    ],
    // Event categories to ignore
    ignoredCategories: [
        'test',
        'debug',
        'development'
    ],
    // Event name patterns to ignore (regex)
    ignoredPatterns: [
        /^test_/,
        /^debug_/,
        /^dev_/
    ]
};

// Helper function to check if event should be ignored
const shouldIgnoreEvent = (payload: any): boolean => {
    // Check exact event name match
    if (payload.event && ANALYTICS_CONFIG.ignoredEvents.includes(payload.event)) {
        console.log(`Event ignored (exact match): ${payload.event}`);
        return true;
    }

    // Check category match
    if (payload.category && ANALYTICS_CONFIG.ignoredCategories.includes(payload.category)) {
        console.log(`Event ignored (category): ${payload.event}, category: ${payload.category}`);
        return true;
    }

    // Check pattern match
    if (payload.event && ANALYTICS_CONFIG.ignoredPatterns.some(pattern => pattern.test(payload.event))) {
        console.log(`Event ignored (pattern): ${payload.event}`);
        return true;
    }

    return false;
};

// Helper function to send analytics data to server
const sendToAnalyticsServer = async (event: 'track' | 'pageview' | 'identify', payload: any) => {
    try {
        // Check if event should be ignored
        if (shouldIgnoreEvent(payload)) {
            return;
        }

        const enrichedPayload = {
            ...payload,
            event: payload.event || event,
            tenant_id: TENANT_ID,
            timestamp: new Date().toISOString(),
            sessionId: sessionStorage.getItem('session_id')
        };

        // Store event locally
        // const learningEvents = JSON.parse(localStorage.getItem('learning_events') || '[]');
        // learningEvents.push(enrichedPayload);

        // // Keep only last 1000 events
        // if (learningEvents.length > 1000) {
        //     learningEvents.splice(0, learningEvents.length - 1000);
        // }

        // localStorage.setItem('learning_events', JSON.stringify(learningEvents));

        // Try to send to server
        await fetch(ANALYTICS_ENDPOINT, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Tenant-ID': TENANT_ID
            },
            body: JSON.stringify({
                ...enrichedPayload,
                ...get(enrichedPayload, "properties"),
            })
        });
    } catch (error) {
        console.warn('Failed to send analytics event to server:', error);
        // Store failed events for retry
        // const failedEvents = JSON.parse(localStorage.getItem('failed_analytics_events') || '[]');
        // failedEvents.push(payload);
        // localStorage.setItem('failed_analytics_events', JSON.stringify(failedEvents));
    }
};

// Create plugins array and filter out null values
const createPlugins = () => {
    const plugins = [
        // Custom plugin for cookie consent
        {
            name: 'cookie-consent',
            initialize: () => {
                console.log('Cookie consent plugin initialized');
            },
            page: ({ payload }) => {
                const hasConsent = getCookie('analytics_consent');
                if (!hasConsent) {
                    // Don't track if no consent
                    console.log('Page tracking skipped - no analytics consent', payload?.title);
                    return false;
                }
                return true;
            },
            track: ({ payload }) => {
                const hasConsent = getCookie('analytics_consent');
                if (!hasConsent) {
                    // Don't track if no consent
                    console.log('Event tracking skipped - no analytics consent', payload?.event);
                    return false;
                }
                return true;
            },
            identify: ({ payload }) => {
                const hasConsent = getCookie('analytics_consent');
                if (!hasConsent) {
                    // Don't identify if no consent
                    console.log('User identification skipped - no analytics consent', payload?.userId);
                    return false;
                }
                return true;
            }
        },

        // Custom plugin for learning analytics
        {
            name: 'learning-analytics',
            page: ({ payload }) => {
                sendToAnalyticsServer('pageview', payload);
                return true;
            },
            track: ({ payload }) => {
                sendToAnalyticsServer('track', payload);
                return true;
            },
            identify: ({ payload }) => {
                sendToAnalyticsServer('identify', payload);
                return true;
            }
        }
    ];

    return plugins.filter(plugin => plugin !== null);
};

// Analytics configuration
const analyticsConfig = {
    app: TENANT_ID,
    version: '1.0.0',
    debug: import.meta.env.VITE_NODE_ENV === 'development',
    plugins: createPlugins()
};

// Initialize analytics with error handling
let analytics;
try {
    analytics = Analytics(analyticsConfig);
    console.log('Analytics initialized successfully for tenant:', TENANT_ID);
} catch (error) {
    console.error('Failed to initialize analytics:', error);
    // Create a fallback analytics object
    analytics = {
        track: () => console.log('Analytics not available - tracking skipped'),
        page: () => console.log('Analytics not available - page tracking skipped'),
        identify: () => console.log('Analytics not available - identification skipped'),
        getState: () => ({}),
        plugins: {}
    };
}

export default analytics; 